import type { LoadFilterOptions } from "@generated/import-openapi.gen";

import type { LoadDebugRecord } from "@prequel/import-react";

import { formatAsUTC } from "@/types";

const splitRecordIDs = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean);

export const parseRecordIDs = (value: string) => {
  const trimmed = value.trim();

  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((id) => typeof id === "string")
          .map((id) => id.trim())
          .filter(Boolean);
      }
    } catch {
      // Not valid JSON yet (likely mid-paste) — fall back to separator parsing.
    }
  }

  return splitRecordIDs(trimmed);
};

// The load endpoint rejects more than this many record IDs in one custom load.
export const MAX_CUSTOM_LOAD_RECORDS = 100;

export type LoadRecordStage = "transform" | "load" | "acknowledge" | "unknown";

export type LoadRecordOutcome = "errored" | "succeeded";

export const LOAD_RECORD_STAGE_LABELS: Record<LoadRecordStage, string> = {
  transform: "Transform",
  load: "Load",
  acknowledge: "Acknowledge",
  unknown: "Unknown",
};

export const LOAD_RECORD_OUTCOME_LABELS: Record<LoadRecordOutcome, string> = {
  errored: "Errored",
  succeeded: "Succeeded",
};

// The debug endpoint returns one row per stage event, so a row names the stage it came from.
export const getLoadRecordStage = (record: LoadDebugRecord): LoadRecordStage => {
  if (record.transform) {
    return "transform";
  }
  if (record.load) {
    return "load";
  }
  if (record.acknowledge) {
    return "acknowledge";
  }

  return "unknown";
};

export const getLoadRecordOutcome = (record: LoadDebugRecord): LoadRecordOutcome =>
  record.transform?.error || record.load?.error || record.acknowledge?.error ? "errored" : "succeeded";

// Stage events repeat a record_id, so rows need the stage to stay unique.
export const getLoadRecordRowID = (record: LoadDebugRecord) => `${record.record_id}:${getLoadRecordStage(record)}`;

export const getSelectedRecordIDs = (rowIDs: (string | number)[]) => [
  ...new Set(rowIDs.map((rowID) => String(rowID).slice(0, String(rowID).lastIndexOf(":")))),
];
export type LoadFilterKind = "time_window" | "records";

export type LoadWindowClock = "last_modified" | "extract_timestamp";

export const LOAD_FILTER_LABELS: Record<LoadFilterKind, string> = {
  time_window: "Time window",
  records: "Specific records",
};

export const LOAD_WINDOW_CLOCK_LABELS: Record<LoadWindowClock, string> = {
  last_modified: "Last modified in source",
  extract_timestamp: "Extract timestamp",
};

export type LoadFilterDraft = {
  kind: LoadFilterKind;
  clock: LoadWindowClock;
  start: string;
  end: string;
  recordIDs: string[];
};

// The endpoint takes exactly one filter object, so a draft maps to a single discriminated shape.
export const buildLoadFilter = ({ kind, clock, start, end, recordIDs }: LoadFilterDraft): LoadFilterOptions => {
  if (kind === "records") {
    return { type: "records", records: { record_ids: recordIDs } };
  }

  const range = { start: formatAsUTC(start), end: formatAsUTC(end) };

  return clock === "last_modified"
    ? { type: "last_modified", last_modified: range }
    : { type: "extract_timestamp", extract_timestamp: range };
};

// A draft stays incomplete until it carries the minimum input its kind needs.
export const isLoadFilterComplete = ({ kind, start, end, recordIDs }: LoadFilterDraft) =>
  kind === "records" ? recordIDs.length > 0 : Boolean(start || end);

// Only actively wrong input gets a message; an untouched draft is incomplete, not wrong.
export const getLoadFilterError = ({ kind, start, end, recordIDs }: LoadFilterDraft): string | undefined => {
  if (kind === "records") {
    return recordIDs.length > MAX_CUSTOM_LOAD_RECORDS
      ? `A custom load takes at most ${MAX_CUSTOM_LOAD_RECORDS} records.`
      : undefined;
  }

  return start && end && start >= end ? "Start time must be before end time." : undefined;
};
